import { stText, stNodes, useStandaloneLanguage } from "../../../i18n/streamingPagesPresentation";
/**
 * ControlCenterLayout -- Shared layout route for all Control Center pages.
 *
 * Provides a unified card shell with a horizontal tab bar
 * (Overview | Server Users | Devices) and renders child routes
 * via <Outlet />.
 *
 * Nested under StreamingLayout in the route tree.
 */
import { useState } from "react";
import { Link, NavLink, Outlet } from "react-router";

import { useFullWidthLayout } from "../../../contexts/LayoutContext";
import { useMe } from "../../../hooks/useMe";
import { useWSDSessions } from "../../../hooks/useWSDSessions";
import { useWSDSettings } from "../../../hooks/useWSDSettings";
import { useSystemApps } from "../../../hooks/useSystemApps";
import { useConfiguredRequestProviders } from "../../../hooks/useConfiguredRequestProviders";
import { useTvGuideNavAvailable } from "../../../hooks/useTvGuideNav";
import { hasPermission } from "../../../lib/permissions";

import { Card } from "../../ui/Card";
import { Icon } from "../../ui/Icon";
import { cn } from "../../../utils/cn";
import { StreamingDashboardHeader } from "../StreamingDashboardHeader";
import { ActivityIndicator } from "../ActivityIndicator";
import { CockpitHeader } from "./CockpitHeader";
import { CapabilitySnapshot } from "./CapabilitySnapshot";
import { UserListPanel } from "../UserListPanel";
import { UserAdminPanel } from "../UserAdminPanel";
import type { CockpitHeaderProps } from "./CockpitHeader";

// ---------------------------------------------------------------------------
// Map the SSE relay status to CockpitHeader's ConnectionStatus enum
// ---------------------------------------------------------------------------

function toCockpitStatus(
  streamStatus: "connecting" | "open" | "closed",
  isServerDegraded: boolean,
): CockpitHeaderProps["connectionStatus"] {
  if (streamStatus === "open") return isServerDegraded ? "error" : "connected";
  if (streamStatus === "connecting") return "reconnecting";
  return "disabled";
}

// ---------------------------------------------------------------------------
// Route tabs (horizontal, top of card)
// ---------------------------------------------------------------------------

const CC_TABS = [
  { get label() { return stText("overview"); }, path: "/streaming/dashboard/control-center", icon: "lucide:gauge", end: true },
  { get label() { return stText("serverUsers"); }, path: "/streaming/dashboard/control-center/server-users", icon: "lucide:users", end: false },
  { get label() { return stText("devices"); }, path: "/streaming/dashboard/control-center/devices", icon: "lucide:monitor-smartphone", end: false },
  { get label() { return stText("historyAnalytics"); }, path: "/streaming/dashboard/control-center/history", icon: "lucide:history", end: false },
] as const;

// The request-manager families that make the Media Requests surface reachable —
// the same set the sidebar's `cc-media-requests` entry declares.
const REQUEST_MANAGER_APPS = ["overseerr", "jellyseerr", "seerr", "ombi"] as const;

// ---------------------------------------------------------------------------
// Layout component
// ---------------------------------------------------------------------------

export const ControlCenterLayout = () => {
  useStandaloneLanguage();
  useFullWidthLayout();

  const { user, appConfig } = useMe();
  const featureEnabled = appConfig?.features?.wsdashboardEnabled ?? false;

  const { status, sessions, stats, reconnect, isServerDegraded, eventStreamStatus } = useWSDSessions();
  const { settings, loading: settingsLoading } = useWSDSettings();
  const serviceConfigured = settings?.embyConfigured || settings?.jellyfinConfigured || settings?.plexConfigured;

  // Canvas-link gating — mirror the sidebar so the tab bar never points at a
  // surface the nav hides. Hooks run unconditionally (before the early returns).
  const { installedApps } = useSystemApps();
  const requestsGrantHeld = hasPermission(user, "admin.requests.read");
  const configuredRequestProviders = useConfiguredRequestProviders(requestsGrantHeld);
  const requestsAvailable =
    requestsGrantHeld &&
    REQUEST_MANAGER_APPS.some((app) => installedApps.has(app) || configuredRequestProviders.has(app));
  const tvGuideAvailable = useTvGuideNavAvailable();

  // Built each render (not memoized) so labels follow the active language, the
  // same way the static `CC_TABS` labels are read fresh in the map below.
  const canvasLinks: { label: string; path: string; icon: string }[] = [
    { label: stText("mediaPortal"), path: "/streaming/dashboard/control-center/media", icon: "lucide:library" },
    ...(requestsAvailable
      ? [{ label: stText("requests"), path: "/streaming/dashboard/control-center/requests", icon: "lucide:inbox" }]
      : []),
    ...(tvGuideAvailable
      ? [{ label: stText("tvGuide"), path: "/streaming/dashboard/control-center/tv-guide", icon: "lucide:calendar-clock" }]
      : []),
  ];

  // Capability snapshot toggle — collapsed by default to keep chrome lean
  const [capabilitySnapshotOpen, setCapabilitySnapshotOpen] = useState(false);

  // User browse flyout + admin panel state
  const [userListOpen, setUserListOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    name: string;
    serverType: string;
  } | null>(null);

  const handleUserSelect = (userId: string, userName: string, serverType: string) => {
    setUserListOpen(false);
    setSelectedUser({ id: userId, name: userName, serverType });
  };

  // ---------------------------------------------------------------------------
  // Feature flag gate
  // ---------------------------------------------------------------------------

  if (!featureEnabled) {
    return (
      <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight min-w-0 break-words hyphens-auto">{stText("controlCenter")}</h1>
          <p className="text-sm text-muted-foreground">{stText("wSDashboardIsNotEnabled")}</p>
        </div>
        <Card title={stText("featureNotEnabled")} description={stText("theControlCenterRequiresWSDashboardToBeEnabled")}>
          <p className="text-sm text-muted-foreground">
            {stNodes("enableControlCenter", { location: <strong key="location">{stText("generalSettingsFeatureFlags")}</strong> })}</p>
        </Card>
      </div>
    );
  }

  // Loading state — show skeleton while WSD settings load
  if (settingsLoading) {
    return (
      <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight min-w-0 break-words hyphens-auto">{stText("controlCenter")}</h1>
          <p className="text-sm text-muted-foreground">{stText("loadingConfiguration2")}</p>
        </div>
        <div className="rounded-lg border border-border/50 bg-card/90 shadow-subtle p-6 animate-pulse space-y-4">
          <div className="h-5 w-48 rounded bg-muted" />
          <div className="h-3 w-72 rounded bg-muted" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-border/20 bg-muted/10 p-4 space-y-3">
                <div className="h-4 w-24 rounded bg-muted" />
                <div className="h-3 w-full rounded bg-muted" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Not configured state
  if (!serviceConfigured) {
    return (
      <div className="mx-auto w-full max-w-[1800px] px-4 sm:px-6 lg:px-8 pb-12">
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-semibold tracking-tight min-w-0 break-words hyphens-auto">{stText("controlCenter")}</h1>
          <p className="text-sm text-muted-foreground">{stText("noMediaServersConfigured")}</p>
        </div>
        <Card>
          <div className="flex flex-col items-center gap-4 py-10 text-center">
            <div className="rounded-full bg-muted/30 p-5">
              <Icon icon="lucide:gauge" className="h-12 w-12 text-muted-foreground/40" aria-hidden />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{stText("configureYourMediaServers")}</h3>
              <p className="mt-1 max-w-md text-sm text-muted-foreground">
                {stText("connectYourEmbyJellyfinOrPlexServerInThe")}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  const cockpitStatus = toCockpitStatus(eventStreamStatus, isServerDegraded);
  const userCount = stats ? Object.keys(stats.byUser).length : null;
  const deviceCount = stats ? Object.keys(stats.byDevice).length : null;

  return (
    <div className="mx-auto w-full max-w-[1800px] px-3 sm:px-6 lg:px-8 pb-12 overflow-x-clip">
      {/* Page header — uses the shared StreamingDashboardHeader component */}
      <StreamingDashboardHeader
        title={stText("controlCenter")}
        connectionStatus={status}
        sessionCount={sessions.length}
        onReconnect={reconnect}
        serviceConfigured={!!serviceConfigured}
        isServerDegraded={isServerDegraded}
        subtitle={
          <span>
            {settings?.embyConfigured && (
              <span className="inline-flex items-center gap-1 mr-2">
                <Icon icon="simple-icons:emby" className="h-3 w-3" aria-hidden />
                Emby
              </span>
            )}
            {settings?.jellyfinConfigured && (
              <span className="inline-flex items-center gap-1 mr-2">
                <Icon icon="simple-icons:jellyfin" className="h-3 w-3" aria-hidden />
                Jellyfin
              </span>
            )}
            {settings?.plexConfigured && (
              <span className="inline-flex items-center gap-1 mr-2">
                <Icon icon="simple-icons:plex" className="h-3 w-3 text-amber-500" aria-hidden />
                Plex
              </span>
            )}
            <span className="text-muted-foreground">&mdash;</span>{" "}
            {stText("manageAndMonitorYourMediaServers")}</span>
        }
      />

      {/* Sticky cockpit header — connection status, server badges, live stats, capability toggle */}
      <CockpitHeader
        connectionStatus={cockpitStatus}
        sessionCount={sessions.length}
        userCount={userCount}
        deviceCount={deviceCount}
        embyConfigured={settings?.embyConfigured ?? false}
        jellyfinConfigured={settings?.jellyfinConfigured ?? false}
        plexConfigured={settings?.plexConfigured ?? false}
        isServerDegraded={isServerDegraded}
        capabilitySnapshotOpen={capabilitySnapshotOpen}
        onCapabilitySnapshotToggle={() => setCapabilitySnapshotOpen((v) => !v)}
        onBrowseUsers={() => setUserListOpen(true)}
      />

      {/* Capability snapshot drawer — collapsed by default */}
      <CapabilitySnapshot
        open={capabilitySnapshotOpen}
        embyConfigured={settings?.embyConfigured ?? false}
        jellyfinConfigured={settings?.jellyfinConfigured ?? false}
        plexConfigured={settings?.plexConfigured ?? false}
      />

      {/* Cross-server user browse flyout */}
      <UserListPanel
        open={userListOpen}
        onOpenChange={setUserListOpen}
        onUserSelect={handleUserSelect}
      />

      {/* Per-user admin panel — opened from UserListPanel row click */}
      <UserAdminPanel
        open={selectedUser !== null}
        onOpenChange={(open) => { if (!open) setSelectedUser(null); }}
        userId={selectedUser?.id ?? ""}
        userName={selectedUser?.name ?? ""}
        serverType={selectedUser?.serverType ?? ""}
      />

      {/* Live activity indicator — shows background task progress from media servers */}
      <div className="mb-4">
        <ActivityIndicator />
      </div>

      {/* Profile-style card shell — @container root so the routed content below
          adapts to the width the shell leaves it, not the viewport. */}
      <div className="@container/cc rounded-lg border border-border/50 bg-card/90 shadow-subtle">
        {/* Horizontal tab bar */}
        <nav className="flex overflow-x-auto qb-scroll whitespace-nowrap items-center border-b border-border/50" aria-label={stText("controlCenterSections")}>
          {CC_TABS.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.end}
              className={({ isActive }) =>
                cn(
                  "relative flex shrink-0 items-center gap-2 px-5 py-3.5 text-sm font-medium transition-colors",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-inset",
                  isActive
                    ? "text-primary-text"
                    : "text-muted-foreground hover:text-foreground",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon icon={tab.icon} className="h-4 w-4" aria-hidden />
                  <span>{tab.label}</span>
                  {isActive && (
                    <span className="absolute bottom-0 left-0 h-0.5 w-full bg-primary" />
                  )}
                </>
              )}
            </NavLink>
          ))}

          {/* Separator + the canvas surfaces, which live outside this shell. */}
          <span className="mx-1 h-5 w-px bg-border/40" aria-hidden />
          {canvasLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={cn(
                "flex shrink-0 items-center gap-1.5 px-3 py-3.5 text-xs font-medium transition-colors",
                "text-muted-foreground hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-inset",
              )}
            >
              <Icon icon={link.icon} className="h-3.5 w-3.5" aria-hidden />
              {link.label}
              <Icon icon="lucide:arrow-up-right" className="h-3 w-3 opacity-50" aria-hidden />
            </Link>
          ))}
        </nav>

        {/* Route-specific content */}
        <Outlet />
      </div>
    </div>
  );
};
